import React from "react";
import ClickableImage from "./ClickableImage";
import PhotoThumbnailWithMarker from "./PhotoThumbnailWithMarker";
import { translate } from "../types/EnumTranslations";
import COLORS, { displayHazards, countWindowsForComponent, countDoorsForComponent, countSheetMetalForComponent, countChimneysForComponent, countDrainageForComponent } from "../Helpers/utils";
import "./ComponentCard.css";
import {BaseComponentImpl} from "../types/implementations";
import { RiskConstructionType, BaseType } from "../types/Enums";
import { Building } from "../types/protocols";

export interface ComponentCardProps {
    component: BaseComponentImpl;
    building: Building;
    buildingKey: string;
    onNavigate: (componentKey: string) => void;
    onMouseEnter?: (componentKey: string) => void;
    onMouseLeave?: () => void;
    isHovered?: boolean;
    isSelected?: boolean;
}


const maxPhotos = 3
const photoSize = 112

const ComponentCard: React.FC<ComponentCardProps> = ({
                                                         component,
                                                         building,
                                                         buildingKey,
                                                         onNavigate,
                                                         onMouseEnter,
                                                         onMouseLeave,
                                                         isHovered,
                                                         isSelected
                                                     }) => {
    return (
        <div
            className={`component-card ${isHovered ? 'highlighted' : ''} ${isSelected ? 'selected' : ''}`}
            style={{
                fontSize: '9pt',
                padding: '20px',
                paddingTop: '26px',
                transform: isHovered ? 'scale(1.02)' : 'scale(1)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                boxShadow: isHovered 
                    ? `0 4px 16px rgba(0,0,0,0.15)${isSelected ? `, 0 0 0 3px ${COLORS.primaryDark}` : ''}` 
                    : isSelected 
                        ? `0 2px 8px rgba(0, 0, 0, 0.1), 0 0 0 3px ${COLORS.primaryDark}` 
                        : '0 2px 8px rgba(0, 0, 0, 0.1)'
            }}
            onClick={() => onNavigate(component.key)}
            onMouseEnter={() => onMouseEnter?.(component.key)}
            onMouseLeave={() => onMouseLeave?.()}
        >
            <div className="component-card-timestamp">
                Uppdaterad {component.lastUpdated()}
            </div>

            <h4>{component.displayName(building)}</h4>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <span className="component-card-label">{translate(component.specificType)}</span>
                {
                    component.material?.value &&
                    (component.material?.value.map((material) => (
                        <span className="component-card-label">{translate(material)}</span>
                    )))
                }

                {
                    component.areaShapes?.value && component.totalArea(building) &&
                    <span className="component-card-label">{component.totalArea(building)} kvm</span>
                }

                {
                    (() => {
                        const windowCount = countWindowsForComponent(component.key, building);
                        return windowCount > 0 ? (
                            <span className="component-card-label">{windowCount} fönster</span>
                        ) : null;
                    })()
                }

                {
                    (() => {
                        const doorCount = countDoorsForComponent(component.key, building);
                        return doorCount > 0 ? (
                            <span className="component-card-label">{doorCount} {doorCount === 1 ? 'dörr' : 'dörrar'}</span>
                        ) : null;
                    })()
                }

                {
                    (() => {
                        // Only show sheet metal count for Roof and Facade components
                        if (component.type === BaseType.Roof || component.type === BaseType.Facade) {
                            const sheetMetalCount = countSheetMetalForComponent(component.key, building);
                            return sheetMetalCount > 0 ? (
                                <span className="component-card-label">{sheetMetalCount} plåtarbeten</span>
                            ) : null;
                        }
                        return null;
                    })()
                }

                {
                    (() => {
                        // Show chimney count for Roof and Facade components
                        if (component.type === BaseType.Roof || component.type === BaseType.Facade) {
                            const chimneyCount = countChimneysForComponent(component.key, building);
                            return chimneyCount > 0 ? (
                                <span className="component-card-label">{chimneyCount} {translate(BaseType.Chimney).toLowerCase()}</span>
                            ) : null;
                        }
                        return null;
                    })()
                }

                {
                    (() => {
                        // Show drainage count for Roof and Facade components
                        if (component.type === BaseType.Roof || component.type === BaseType.Facade) {
                            const drainageCount = countDrainageForComponent(component.key, building);
                            return drainageCount > 0 ? (
                                <span className="component-card-label">{drainageCount} {translate(BaseType.RoofDrainage).toLowerCase()}</span>
                            ) : null;
                        }
                        return null;
                    })()
                }
            </div>

            {
                component.hazardous?.value &&
                <span className="component-card-label" style={{ color: "white", backgroundColor: COLORS.deepRed, marginTop: '8px' }}>
                    {displayHazards((component.hazardous.value))}
                </span>
            }

            {
                component.buildingRisks?.value && component.buildingRisks.value.risks && component.buildingRisks.value.risks.length > 0 &&
                <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {component.buildingRisks.value.risks.map((risk: RiskConstructionType, index: number) => (
                        <span 
                            key={index}
                            className="component-card-label" 
                            style={{ 
                                color: "white", 
                                backgroundColor: COLORS.redOrange,
                                fontSize: '8pt'
                            }}
                        >
                            {translate(risk)}
                        </span>
                    ))}
                    {component.buildingRisks.value.customRisk && (
                        <span 
                            className="component-card-label" 
                            style={{ 
                                color: "white", 
                                backgroundColor: COLORS.redOrange,
                                fontSize: '8pt'
                            }}
                        >
                            {component.buildingRisks.value.customRisk}
                        </span>
                    )}
                </div>
            }

            {
                component.photos &&
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: '20px' }}>
                    {component.photos && buildingKey && Object.entries(component.photos).slice(0, maxPhotos).map(([key, photo]) => (
                        <ClickableImage
                            key={key}
                            buildingKey={buildingKey}
                            photo={photo}
                            disableClick={true}
                            size={photoSize}
                        />
                    ))}
                    {component.photos && Object.keys(component.photos).length > maxPhotos && (
                        <div
                            style={{
                                width: `${photoSize}px`,
                                height: `${photoSize}px`,
                                backgroundColor: '#cccccc',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14pt',
                                borderRadius: '4px'
                            }}
                        >
                            +{Object.keys(component.photos).length - maxPhotos}
                        </div>
                    )}
                </div>
            }

            {
                component.photoLocation &&
                <div style={{ marginTop: '20px' }}>
                    <div style={{ fontSize: '8pt', color: '#666', marginBottom: '4px' }}>Position i foto</div>
                    <PhotoThumbnailWithMarker 
                        photoLocation={component.photoLocation}
                        size={photoSize}
                    />
                </div>
            }
        </div>
    );
};

export default ComponentCard;